'use client';

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, Suspense } from 'react';
import { useSession } from 'next-auth/react';
import { usePathname, useRouter } from 'next/navigation';
import type { OrganizationRouteDescriptor } from '@core/helpers/profile/OrganizationRouting';
import { getOrganizationDefaultHref, resolveOrganizationFromPath } from '@core/helpers/profile/OrganizationRouting';
import { useOrgContext } from '@organizations/ui/OrgContext';
import { useAppReadyOptional } from '@core/helpers/react/contexts/AppReadyContext';

interface VaultApiEntry {
  id?: string;
  slug: string;
  title?: string;
  href?: string;
  visibility?: 'public' | 'private' | 'protected' | 'org';
}

interface ApiOrganizationEntry {
  id?: string;
  name?: string;
  slug?: string;
  logoUrl?: string | null;
  isPrimary?: boolean;
  organizationRole?: string | null;
  vaults?: VaultApiEntry[];
}

interface VaultsResponse {
  organizations?: ApiOrganizationEntry[];
}

export interface ProfileOrganizationVaultLink {
  id: string;
  slug: string;
  title: string;
  href: string;
  visibility: 'public' | 'private' | 'protected' | 'org';
}

export interface ProfileOrganization extends OrganizationRouteDescriptor {
  name: string;
  logoUrl: string | null;
  organizationRole: string | null;
  vaults: ProfileOrganizationVaultLink[];
}

interface ProfileOrganizationContextValue {
  organizations: ProfileOrganization[];
  currentOrganization: ProfileOrganization | null;
  primaryOrganization: ProfileOrganization | null;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  selectOrganization: (organizationId: string) => void;
}

const ProfileOrganizationContext = createContext<ProfileOrganizationContextValue | null>(null);

export function ProfileOrganizationProvider({ children }: { children: React.ReactNode }) {
  const router = useRouter();
  const pathname = usePathname() ?? '/';
  const { status } = useSession();
  const fetchRequestRef = useRef(0);
  const [organizations, setOrganizations] = useState<ProfileOrganization[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const appReady = useAppReadyOptional();

  // Server-side org context (set by OrgContextSetter in layouts)
  const serverOrgContext = useOrgContext();

  const fetchOrganizations = useCallback(async () => {
    const requestId = ++fetchRequestRef.current;
    setLoading(true);
    try {
      const response = await fetch('/api/vaults/me', {
        credentials: 'include',
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) {
        throw new Error(`Failed to load organizations (status ${response.status})`);
      }
      const payload = (await response.json()) as VaultsResponse;
      if (fetchRequestRef.current !== requestId) {
        return;
      }
      const normalized = normalizeOrganizations(payload.organizations ?? []);
      setOrganizations(normalized);
      setError(null);
    } catch (err) {
      if (fetchRequestRef.current !== requestId) {
        return;
      }
      const message = err instanceof Error ? err.message : 'Failed to load organizations.';
      setOrganizations([]);
      setError(message);
    } finally {
      if (fetchRequestRef.current === requestId) {
        setLoading(false);
      }
    }
  }, []);

  const refresh = useCallback(async () => {
    await fetchOrganizations();
  }, [fetchOrganizations]);

  useEffect(() => {
    if (status === 'loading') {
      return;
    }
    void fetchOrganizations();
  }, [status, fetchOrganizations]);

  // Signal 'organizations' ready when loading is complete
  useEffect(() => {
    if (!loading) {
      appReady?.signalReady('organizations');
    }
  }, [loading, appReady]);

  useEffect(() => {
    const handler = () => {
      void refresh();
    };
    window.addEventListener('vaults:updated', handler);
    return () => window.removeEventListener('vaults:updated', handler);
  }, [refresh]);

  const primaryOrganization = useMemo(() => {
    if (!organizations.length) {
      return null;
    }
    return organizations.find((org) => org.isPrimary) ?? organizations[0] ?? null;
  }, [organizations]);

  const currentOrganization = useMemo(() => {
    // Server-side org context takes precedence (set by layouts for validated orgs)
    if (serverOrgContext.orgSlug) {
      // Try to find the org in our list
      const serverOrg = organizations.find((org) => org.slug === serverOrgContext.orgSlug);
      if (serverOrg) {
        return serverOrg;
      }
      // Org exists (validated by server) but not in API response (no public vaults for unauth)
      // Create a minimal entry so menu shows org context
      if (serverOrgContext.orgId) {
        return {
          id: serverOrgContext.orgId,
          slug: serverOrgContext.orgSlug,
          name: serverOrgContext.orgSlug.replace(/-/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase()),
          logoUrl: null,
          isPrimary: false,
          organizationRole: null,
          vaults: [],
        };
      }
    }

    // Fall back to path-based resolution
    if (!organizations.length) {
      return null;
    }
    const routeMatch = resolveOrganizationFromPath(pathname, organizations, primaryOrganization);
    if (!routeMatch) {
      return primaryOrganization ?? null;
    }
    const normalized = organizations.find((org) => org.slug === routeMatch.slug) ?? null;
    return normalized ?? primaryOrganization ?? organizations[0] ?? null;
  }, [organizations, pathname, primaryOrganization, serverOrgContext.orgSlug, serverOrgContext.orgId]);

  const selectOrganization = useCallback(
    (organizationId: string) => {
      const target = organizations.find((org) => org.id === organizationId);
      if (!target) {
        return;
      }
      const nextPath = getOrganizationDefaultHref(target);
      if (nextPath && nextPath !== pathname) {
        router.push(nextPath);
      }
    },
    [organizations, pathname, router],
  );

  const contextValue = useMemo<ProfileOrganizationContextValue>(
    () => ({
      organizations,
      currentOrganization,
      primaryOrganization,
      loading: loading || status === 'loading',
      error,
      refresh,
      selectOrganization,
    }),
    [organizations, currentOrganization, primaryOrganization, loading, status, error, refresh, selectOrganization],
  );

  return (
    <ProfileOrganizationContext.Provider value={contextValue}>
      {children}
    </ProfileOrganizationContext.Provider>
  );
}

export function useProfileOrganizations(): ProfileOrganizationContextValue {
  const context = useContext(ProfileOrganizationContext);
  if (!context) {
    throw new Error('useProfileOrganizations must be used within a ProfileOrganizationProvider');
  }
  return context;
}

function normalizeOrganizations(entries: ApiOrganizationEntry[]): ProfileOrganization[] {
  return entries
    .map((entry, index) => {
      const slug = entry.slug ?? '';
      const vaults = (entry.vaults ?? [])
        .filter((vault): vault is VaultApiEntry & { href: string } => Boolean(vault.slug) && Boolean(vault.href))
        .map((vault, idx) => ({
          id: vault.id ?? `${slug}-${vault.slug}-${idx}`,
          slug: vault.slug,
          title: vault.title ?? vault.slug,
          href: vault.href!,
          visibility: vault.visibility ?? 'public',
        }));
      return {
        id: entry.id ?? `${slug}-${index}`,
        name: entry.name ?? (slug || 'Organization'),
        slug,
        logoUrl: entry.logoUrl ?? null,
        isPrimary: Boolean(entry.isPrimary),
        organizationRole: entry.organizationRole ?? null,
        vaults,
      };
    })
    .filter((entry) => entry.slug.length > 0);
}
